// src/App.jsx
import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { esES } from '@mui/material/locale';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setEmpresas, initEmpresaActiva } from './redux/empresaSlice';
import AuthWrapper from './components/auth/AuthWrapper';
import { useAuth } from './components/auth/AuthContext';
import NotificationProvider from './contexts/NotificationProvider';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { limpiarRut } from './utils/rutValidator';

// Componentes de Layout
import MenuPrincipal from './components/layout/MenuPrincipal';

// Paginas
import ObtencionTokenTab from './components/rcv/ObtencionTokenTab';
import ConsultaRcvTab from './components/rcv/ConsultaRcvTab';
import DocumentosTab from './components/rcv/DocumentosTab';
import AsociacionesTab from './components/rcv/AsociacionesTab';
import EmpresasPage from './components/empresas/EmpresasPage';
import CertificadosPage from './components/empresas/CertificadosPage';
import ConfigTributariaPage from './components/empresas/ConfigTributariaPage';
import ConfiguracionPage from './components/configuracion/ConfiguracionPage';
import CsvUploadTab from './components/rcv/CsvUploadTab';
import ExportacionCtbTab from './components/ctb/ExportacionCtb'
import CatalogosTab from './components/configuracion/CatalogosTab';
import TesoreríaTab from './components/tesoreria/TesoreríaTab';
import PescaPage from './components/pesca/PescaPage';
import VisadorPage from './components/visador/VisadorPage';
import ResumenRcvPage from './components/rcv/ResumenRcvPage';
import BitacoraPage from './components/bitacora/BitacoraPage';
import EmisionDTEPage from './components/emision/EmisionDTEPage';
import CertificacionDashboard from './components/certificacion/CertificacionDashboard';

// Crear un tema personalizado con colores corporativos
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#4791db',
      dark: '#115293',
    },
    secondary: {
      main: '#ff6b00',
      light: '#ff8c33',
      dark: '#cc5500',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
}, esES);

// Componente para rutas protegidas que requieren autenticación
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector(state => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/autenticacion" replace />;
  }

  return children;
};

// Componente para rutas que requieren un permiso específico
const PermissionRoute = ({ children, permiso }) => {
  const { user } = useAuth();
  if (!user?.permissions?.[permiso] && !user?.is_superuser) return <Navigate to="/" replace />;
  return children;
};

// Componente para rutas que requieren empresa seleccionada
const EmpresaRoute = ({ children }) => {
  const { empresaActiva } = useSelector(state => state.empresa);

  if (!empresaActiva) {
    return <Navigate to="/empresas" replace />;
  }

  return children;
};

/**
 * Convierte una organización UAU al formato empresa del sistema
 */
function mapOrgToEmpresa(org) {
  const { rut: rutNum, dv } = limpiarRut(org.rut || '');
  return {
    id: org.id,
    rut: rutNum,
    dv: dv,
    razon_social: org.name || org.nombre || '',
    nombre_corto: org.short_name || org.name || '',
    rut_completo: org.rut || '',
    activa: true
  };
}

/**
 * Componente interno que carga organizaciones UAU al Redux store
 */
function OrganizationLoader({ children }) {
  const dispatch = useDispatch();
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.organizations) {
      const empresas = user.organizations.map(mapOrgToEmpresa);
      dispatch(setEmpresas(empresas));
    }
    dispatch(initEmpresaActiva());
  }, [user, dispatch]);

  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthWrapper>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NotificationProvider>
            <OrganizationLoader>
              <Router>
                <MenuPrincipal>
                  <Routes>
                    <Route path="/" element={<ObtencionTokenTab />} />
                    <Route path="/autenticacion" element={<ObtencionTokenTab />} />

                    <Route path="/rcv/consulta" element={
                      <ProtectedRoute><EmpresaRoute><ConsultaRcvTab /></EmpresaRoute></ProtectedRoute>
                    } />
                    <Route path="/rcv/csv-upload" element={
                      <EmpresaRoute><CsvUploadTab /></EmpresaRoute>
                    } />
                    <Route path="/rcv/documentos" element={
                      <EmpresaRoute><DocumentosTab /></EmpresaRoute>
                    } />
                    <Route path="/rcv/asociaciones" element={
                      <EmpresaRoute><AsociacionesTab /></EmpresaRoute>
                    } />
                    <Route path="/rcv/resumen" element={
                      <PermissionRoute permiso="rcv.resumen"><EmpresaRoute><ResumenRcvPage /></EmpresaRoute></PermissionRoute>
                    } />
                    <Route path="/rcv/exportacion" element={
                      <EmpresaRoute><ExportacionCtbTab /></EmpresaRoute>
                    } />
                    <Route path="/tesoreria" element={
                      <EmpresaRoute><TesoreríaTab /></EmpresaRoute>
                    } />
                    <Route path="/pesca" element={
                      <EmpresaRoute><PescaPage /></EmpresaRoute>
                    } />
                    <Route path="/visar" element={
                      <PermissionRoute permiso="rcv.visador"><VisadorPage /></PermissionRoute>
                    } />
                    <Route path="/configuracion" element={
                      <EmpresaRoute><ConfiguracionPage /></EmpresaRoute>
                    } />
                    <Route path="/bitacora" element={
                      <PermissionRoute permiso="rcv.bitacora"><BitacoraPage /></PermissionRoute>
                    } />
                    <Route path="/empresas" element={<EmpresasPage />} />
                    <Route path="/certificados" element={<CertificadosPage />} />
                    <Route path="/certificados/:rutEmpresa" element={<CertificadosPage />} />
                    <Route path="/config-tributaria/:rutEmpresa" element={<ConfigTributariaPage />} />
                    <Route path="/emision" element={
                      <PermissionRoute permiso="emision"><EmisionDTEPage /></PermissionRoute>
                    } />
                    <Route path="/certificacion" element={<CertificacionDashboard />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </MenuPrincipal>
              </Router>
            </OrganizationLoader>
          </NotificationProvider>
        </ThemeProvider>
      </AuthWrapper>
    </ErrorBoundary>
  );
}

export default App;
